import * as React from 'react';
import { isMiniFeatureEnabled } from 'owa-mini-feature-flags';
import { IStyle } from '@uifabric/merge-styles';
import { mergeOverrideStyles } from 'mini-sass-mixins/lib/utils/mergeOverrideStyles';
import {
    ColorScheme,
    colorSchemeToCss,
    getAccessibleTextColor,
    AccessibleTextSchemeBackground,
} from 'mini-color-utils/lib/ColorScheme';

const styles = require('./Button.scss');
const classNames = require('classnames/bind').bind(styles);

export enum ButtonType {
    'Default',
    'Primary',
    'Inline',
    'InlinePrimary',
    'Link',
    'CommandBar',
}

export interface ButtonProps {
    /** Text to show on the button */
    text?: string;
    /** Type of the button */
    type?: ButtonType;
    /** Classname */
    className?: any;
    /** Custom renderer for icon */
    onRenderIcon?: () => JSX.Element;
    /** Icon style */
    icon?: string;
    /** on click event handler */
    onClick?: (ev?: React.MouseEvent<HTMLButtonElement>) => void;
    /** on key down event handler */
    onKeyDown?: (ev?: React.KeyboardEvent<HTMLButtonElement>) => void;
    /** Color scheme */
    colorScheme?: ColorScheme;
    /** aria label */
    ariaLabel?: string;
    /** if it invokes a popup label */
    hasPopup?: boolean;
    /** tabIndex value */
    tabIndex?: number;
    /**
     * if it is hidden for accessibility
     */
    isAriaHidden?: boolean;

    onTouchStart?: (ev?: React.TouchEvent<HTMLButtonElement>) => void;
    onTouchEnd?: (ev?: React.TouchEvent<HTMLButtonElement>) => void;
}

export default React.forwardRef(function Button(props: ButtonProps, ref?: React.RefObject<any>) {
    const {
        type,
        className: additionalClassName,
        text,
        onClick: onButtonClick,
        onKeyDown,
        onRenderIcon,
        icon,
        tabIndex,
        colorScheme,
        ariaLabel = props.text || '',
        hasPopup = false,
        isAriaHidden = false,
        onTouchStart,
        onTouchEnd,
    } = props;

    const ariaHasPopupProps = hasPopup ? { 'aria-haspopup': true } : {};
    const ariaHiddenProps = isAriaHidden ? { 'aria-hidden': true } : {};
    const onClick = (ev?: React.MouseEvent<HTMLButtonElement>) => {
        onButtonClick?.(ev);
        ev?.stopPropagation();
    };

    return (
        <button
            ref={ref}
            {...ariaHasPopupProps}
            onClick={onClick}
            onKeyDown={onKeyDown}
            aria-label={ariaLabel}
            title={ariaLabel}
            tabIndex={tabIndex ?? 0}
            {...ariaHiddenProps}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            className={classNames(
                styles.button,
                _getButtonClassName(),
                mergeOverrideStyles(_getButtonStyleOverride()),
                additionalClassName
            )}>
            {onRenderIcon
                ? onRenderIcon()
                : icon && <i className={classNames(styles.icon, icon)} />}
            {text}
        </button>
    );

    function _getButtonClassName() {
        let className: any;
        switch (type) {
            case ButtonType.Primary:
                className = styles.primary;
                break;

            case ButtonType.Inline:
                className = styles.inline;
                break;

            case ButtonType.InlinePrimary:
                className = styles.inlinePrimary;
                break;

            case ButtonType.Link:
                className = styles.link;
                break;

            case ButtonType.CommandBar:
                className = styles.commandBar;
                break;

            default:
                className = styles.default;
                break;
        }

        return additionalClassName ? classNames(additionalClassName, className) : className;
    }

    function _getButtonStyleOverride() {
        if (!colorScheme) {
            return null;
        }

        let style: {};
        switch (type) {
            case ButtonType.Primary:
            case ButtonType.InlinePrimary:
                style = {
                    ...colorSchemeToCss(colorScheme),
                    ..._getActiveStateStyle({ background: colorScheme.schemeDark }),
                };
                break;

            case ButtonType.CommandBar:
                style = {
                    color: colorScheme.schemeDarker,
                    ..._getActiveStateStyle({ background: colorScheme.schemeLighter }),
                };
                break;

            default:
                style = {
                    color: getAccessibleTextColor(
                        colorScheme,
                        AccessibleTextSchemeBackground.msColorWhite
                    ),
                    borderColor: colorScheme.schemeLight,
                    ..._getActiveStateStyle({
                        borderColor: colorScheme.schemeLighter,
                        background: colorScheme.schemeLighter,
                    }),
                };
                break;
        }

        return style;
    }

    function _getActiveStateStyle(style: IStyle) {
        return isMiniFeatureEnabled('mini-tapFeedback')
            ? {
                  selectors: {
                      ':active': style,
                  },
              }
            : {};
    }
});
